import { supabase } from '../integrations/supabase/client';
import { products as staticProducts, type Product, type ProductCategory } from '../data/products';

export interface ProductRow {
  id: string;
  title: string;
  description: string;
  category: ProductCategory;
  price_value: number;
  image: string;
  hours: number | null;
  students: number | null;
  rating: number | null;
  author: string | null;
  downloads: string | null;
  download_url: string | null;
  whatsapp_link: string | null;
  is_published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProductInput {
  title: string;
  description: string;
  category: ProductCategory;
  price_value: number;
  image: string;
  hours: number | null;
  students: number | null;
  rating: number | null;
  author: string | null;
  downloads: string | null;
  download_url: string | null;
  whatsapp_link: string | null;
  is_published: boolean;
  sort_order: number;
}

const categoryValues: ProductCategory[] = ['whatsapp-class', 'short-course', 'ebook', 'resource'];

export function isProductCategory(value: string): value is ProductCategory {
  return categoryValues.includes(value as ProductCategory);
}

export function productFromRow(row: ProductRow): Product {
  const priceValue = Number(row.price_value) || 0;
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    category: isProductCategory(row.category) ? row.category : 'short-course',
    isFree: priceValue === 0,
    price: priceValue === 0 ? 'FREE' : `₦${priceValue.toLocaleString('en-NG')}`,
    priceValue,
    image: row.image,
    hours: row.hours ?? undefined,
    students: row.students ?? undefined,
    rating: row.rating == null ? undefined : Number(row.rating),
    author: row.author ?? undefined,
    downloads: row.downloads ?? undefined,
    downloadUrl: row.download_url ?? undefined,
    whatsappLink: row.whatsapp_link ?? undefined,
  };
}

export function productInputFromRow(row: ProductRow): ProductInput {
  return {
    title: row.title,
    description: row.description,
    category: isProductCategory(row.category) ? row.category : 'short-course',
    price_value: Number(row.price_value) || 0,
    image: row.image,
    hours: row.hours,
    students: row.students,
    rating: row.rating == null ? null : Number(row.rating),
    author: row.author,
    downloads: row.downloads,
    download_url: row.download_url,
    whatsapp_link: row.whatsapp_link,
    is_published: row.is_published,
    sort_order: row.sort_order,
  };
}

export async function listPublishedProducts(): Promise<Product[]> {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('is_published', true)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as ProductRow[]).map(productFromRow);
}

export async function listProductsAdmin(): Promise<ProductRow[]> {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ProductRow[];
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 64) || 'product';

async function uniqueProductId(title: string): Promise<string> {
  const base = slugify(title);
  const { data } = await supabase.from('products').select('id').eq('id', base).maybeSingle();
  return data ? `${base}-${Date.now().toString(36)}` : base;
}

export async function createProductAdmin(input: ProductInput): Promise<string> {
  const id = await uniqueProductId(input.title);
  const { error } = await supabase.from('products').insert({ id, ...input });
  if (error) throw new Error(error.message);
  return id;
}

export async function updateProductAdmin(id: string, input: ProductInput): Promise<void> {
  const { error } = await supabase.from('products').update(input).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function setProductPublishedAdmin(id: string, isPublished: boolean): Promise<void> {
  const { error } = await supabase.from('products').update({ is_published: isPublished }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteProductAdmin(id: string): Promise<void> {
  const { error } = await supabase.from('products').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/** Used only when the products table is unavailable (e.g. local preview before migration). */
export function staticProductFallback(): Product[] {
  return staticProducts;
}
